/**
 * The engine's view of an Entry a Character holds (a Selection, or an extra Entry such as a
 * Condition fed in by play mode). Mirrors the parts of Foundry's Item the Rule Elements read:
 * `system`, `flags.pf2e.rulesSelections`, level, traits, and the roll options the item emits.
 */
import type { Entry } from "../../shared/snapshot-format";
import type { AnswerValue } from "../character/model";
import { sluggify } from "./slug";
import { FEATURE_CATEGORIES, PHYSICAL_ITEM_TYPES } from "./values";

export type Answers = Record<string, AnswerValue>;

export interface EngineItemInput {
  entry: Entry;
  /** Answers to the prompts the Entry asked (ChoiceSet flag -> value, boosts, key ability). */
  answers?: Answers | undefined;
  /** The Choice this Entry fills, for reporting. */
  choiceKey?: string | undefined;
  /** The Level at which the Entry was taken (a feat's `location` level in Foundry). */
  levelTaken?: number | undefined;
  /** Badge value for Conditions and Effects (play mode): a Condition's value, an Effect's counter. */
  badge?: number | undefined;
  /** An Effect's level when it differs from the Entry's (a heightened spell's rank); Foundry sets `system.level.value` on the copy. */
  level?: number | undefined;
  /** The Session item this Entry stands for (#18), for linking grants back. */
  sessionId?: string | undefined;
  /** The Session id of the item that granted this one (#18); the engine links `grantedBy` from it. */
  grantedBySessionId?: string | undefined;
  /** Persistent damage data for a persistent-damage Condition (Foundry: `system.persistent`). */
  persistent?: { formula: string; damageType: string; dc: number } | undefined;
  /** The Entry (and Choice) that granted this one, when it was granted (#6). */
  grantedBy?: EngineItem | null | undefined;
  /** ChoiceSet flags whose answers a GrantItem preselected: they skip candidate validation, as in Foundry. */
  preselected?: Iterable<string> | undefined;
}

export type ItemRole = "ancestry" | "heritage" | "background" | "class" | "other" | "physical" | "temporary";

const MAGIC_TRADITIONS = ["arcane", "divine", "occult", "primal"];

export class EngineItem {
  readonly entry: Entry;
  readonly uuid: string;
  readonly id: string;
  /** The Entry's name, adjusted by a ChoiceSet's `adjustName` or a GrantItem alteration. */
  name: string;
  readonly slug: string;
  readonly type: string;
  readonly system: Record<string, unknown>;
  readonly flags: { pf2e: Record<string, unknown>; system: Record<string, unknown>; core: Record<string, unknown> };
  readonly answers: Answers;
  readonly choiceKey: string | undefined;
  readonly levelTaken: number | undefined;
  badge: { value: number } | null;
  readonly role: ItemRole;
  readonly sessionId: string | null;
  readonly grantedBySessionId: string | null;
  /**
   * A Condition another Condition deactivated (a lower value of the same kind, or one its
   * `overrides` name); its Rule Elements are ignored (Foundry: `ConditionPF2e#active`).
   */
  active = true;
  /** Names of the Conditions that deactivated this one. */
  readonly overriddenBy: string[] = [];
  /** Extra roll options a Rule Element on this item adds to it (Foundry: `item.specialOptions`). */
  readonly specialOptions: string[] = [];
  /** The item that granted this one (Foundry: `item.grantedBy`). */
  grantedBy: EngineItem | null;
  readonly preselected: Set<string>;

  constructor(input: EngineItemInput) {
    const entry = input.entry;
    this.entry = entry;
    this.uuid = entry.uuid;
    this.id = entry.id;
    this.name = entry.name;
    this.type = entry.type;
    this.slug = entry.slug || sluggify(entry.name);
    // Rule Elements read (and ActiveEffectLike may write) item data: work on a copy.
    this.system = structuredClone(entry.system);
    this.answers = { ...(input.answers ?? {}) };
    const pf2e: Record<string, unknown> = { ...structuredClone(entry.flags?.pf2e ?? {}), rulesSelections: { ...this.answers } };
    this.flags = { pf2e, system: pf2e, core: {} };
    this.choiceKey = input.choiceKey;
    this.levelTaken = input.levelTaken;
    this.badge = typeof input.badge === "number" ? { value: input.badge } : null;
    this.sessionId = input.sessionId ?? null;
    this.grantedBySessionId = input.grantedBySessionId ?? null;
    if (entry.type === "condition") {
      // Foundry keeps `value.value` and the badge in step; an unvalued Condition has null.
      const value = this.system.value as { isValued?: boolean; value?: unknown } | undefined;
      if (value && typeof value === "object") {
        if (this.badge && value.isValued) value.value = this.badge.value;
        else if (value.isValued) this.badge = { value: typeof value.value === "number" ? value.value : 1 }, (value.value = this.badge.value);
        else value.value = null;
      }
      if (input.persistent) this.system.persistent = { ...input.persistent, criticalHit: false };
    } else if (entry.type === "effect") {
      const badge = this.system.badge as { type?: string; value?: unknown } | null | undefined;
      if (this.badge && badge && typeof badge === "object") badge.value = this.badge.value;
      else if (!this.badge && badge && typeof badge === "object" && typeof badge.value === "number" && badge.type !== "formula") this.badge = { value: badge.value };
    }
    if (typeof input.level === "number") this.system.level = { ...(typeof this.system.level === "object" && this.system.level !== null ? this.system.level : {}), value: input.level };
    this.grantedBy = input.grantedBy ?? null;
    this.preselected = new Set(input.preselected ?? []);
    this.role =
      entry.type === "ancestry" || entry.type === "heritage" || entry.type === "background" || entry.type === "class"
        ? entry.type
        : entry.type === "condition" || entry.type === "effect" || entry.type === "affliction"
          ? "temporary"
          : (PHYSICAL_ITEM_TYPES as readonly string[]).includes(entry.type)
            ? "physical"
            : "other";
  }

  /** Foundry's `_id`, for `{item|_id}` placeholders. */
  get _id(): string {
    return this.id;
  }

  /** A record of the alterations applied to this copy (ItemAlteration, grant alterations), for the sheet. */
  applied: string[] = [];
  /** Created inside the engine for this derive only (an in-memory Condition, an aura's own Effect), never a Selection. */
  inMemory = false;

  /** Foundry's `rollOptionSlug` for Conditions and Effects: the slug without an "effect-" / "stance-" style prefix. */
  get rollOptionSlug(): string {
    return this.slug.replace(/^(?:[a-z]+-)?(?:effect|stance)-/, "");
  }

  /** Persistent damage data on a persistent-damage Condition. */
  get persistent(): { formula: string; damageType: string; dc: number } | null {
    const p = this.system.persistent;
    return typeof p === "object" && p !== null && typeof (p as { damageType?: unknown }).damageType === "string" ? (p as { formula: string; damageType: string; dc: number }) : null;
  }

  /** Foundry's `ConditionPF2e#key`: persistent damage splits by damage type. */
  get conditionKey(): string {
    return this.persistent ? `persistent-damage-${this.persistent.damageType}` : this.slug;
  }

  /** Foundry's `Item#description`: the description text, for `{item|description}` placeholders. */
  get description(): string {
    const value = (this.system.description as { value?: unknown } | undefined)?.value;
    return typeof value === "string" ? value : "";
  }

  get level(): number | null {
    const level = (this.system.level as { value?: unknown } | undefined)?.value;
    return typeof level === "number" ? level : null;
  }

  get traits(): string[] {
    const traits = (this.system.traits as { value?: unknown } | undefined)?.value;
    return Array.isArray(traits) ? traits.filter((t): t is string => typeof t === "string") : [];
  }

  get rarity(): string | null {
    const rarity = (this.system.traits as { rarity?: unknown } | undefined)?.rarity;
    return typeof rarity === "string" ? rarity : null;
  }

  get otherTags(): string[] {
    const tags = (this.system.traits as { otherTags?: unknown } | undefined)?.otherTags;
    return Array.isArray(tags) ? tags.filter((t): t is string => typeof t === "string") : [];
  }

  get category(): string | null {
    const category = this.system.category;
    return typeof category === "string" ? category : null;
  }

  get isFeature(): boolean {
    return this.type === "feat" && (FEATURE_CATEGORIES as readonly string[]).includes(this.category ?? "");
  }

  get rules(): Record<string, unknown>[] {
    const rules = this.system.rules;
    return Array.isArray(rules) ? rules.filter((r): r is Record<string, unknown> => typeof r === "object" && r !== null) : [];
  }

  /** The chain of granting items above this one, outermost first (empty when taken directly). */
  get grantChain(): EngineItem[] {
    const chain: EngineItem[] = [];
    const seen = new Set<EngineItem>();
    for (let item = this.grantedBy; item && !seen.has(item); item = item.grantedBy) {
      seen.add(item);
      chain.unshift(item);
    }
    return chain;
  }

  /** Roll options describing this item, prefixed (`item`, `parent`, `origin:item` ...): Foundry's `Item#getRollOptions`. */
  getRollOptions(prefix = this.type, includeGranter = true): string[] {
    const isFeature = this.isFeature;
    if (prefix === "feat" && isFeature) prefix = "feature";
    const traits = this.traits;
    const granterOptions = includeGranter && this.grantedBy ? this.grantedBy.getRollOptions("granter", false).map((o) => `${prefix}:${o}`) : [];
    const options = [
      `${prefix}:id:${this.id}`,
      `${prefix}:${this.type === "effect" ? this.slug.replace(/^(?:spell-)?(?:effect|stance)-/, "") : this.slug}`,
      `${prefix}:slug:${this.slug}`,
      ...granterOptions,
      ...this.specialOptions.map((o) => `${prefix}:${o}`),
      ...this.otherTags.map((t) => `${prefix}:tag:${t}`),
      ...traits.map((t) => `${prefix}:trait:${t}`),
    ];
    const rarity = this.rarity;
    if (rarity && (this.type !== "feat" || !isFeature)) options.push(`${prefix}:rarity:${rarity}`);
    if (this.badge) options.push(`${prefix}:badge:value:${this.badge.value}`);
    if (this.role === "temporary" && this.badge) options.push(`${prefix}:badge:type:counter`);
    const persistent = this.persistent;
    if (persistent) options.push(`damage:type:${persistent.damageType}`, `${prefix}:damage:type:${persistent.damageType}`);
    if (this.type === "spell" || traits.some((t) => t === "magical" || MAGIC_TRADITIONS.includes(t))) options.push(`${prefix}:magical`);
    const level = this.level;
    if (typeof level === "number" && !(this.type === "feat" && level === 0)) options.push(`${prefix}:level:${level}`);
    const itemType = isFeature ? "feature" : this.type;
    if (prefix !== itemType) options.unshift(`${prefix}:type:${itemType}`);
    if (this.type === "feat" && this.category) options.push(`${prefix}:category:${this.category}`);
    return options;
  }
}
